/**
 * CATALOG-PIM-145A-R1D — Store V2 merges durable manager inventory overlay
 * (same-origin Pages proxy → Worker R2). Reserved qty never shown to customer.
 * quantity=0 → unavailable label; product stays in catalog.
 */
import type { CatalogV2Product } from "./catalog-v2-types.js";

export type CustomerInventoryOverlayRow = {
  quantity: number;
  reserved_quantity: number;
  available_quantity: number;
  stock_status: string;
  label_ua: string;
  inventory_source: string;
  updated_at: string;
};

export type CustomerInventoryOverlayFile = {
  schema_version: "customer_availability_overlay_v1";
  updated_at: string;
  cache_version: string;
  by_product: Record<string, CustomerInventoryOverlayRow>;
};

let overlayCache: Promise<CustomerInventoryOverlayFile | null> | null = null;
let overlayCacheVersion: string | null = null;

export function invalidateCustomerInventoryOverlayCache(): void {
  overlayCache = null;
  overlayCacheVersion = null;
}

async function fetchOverlay(cacheVersion?: string | null): Promise<CustomerInventoryOverlayFile | null> {
  const v = cacheVersion ? `?v=${encodeURIComponent(cacheVersion)}` : `?t=${Date.now()}`;
  try {
    const res = await fetch(`/api/store-v2/availability-overlay${v}`, {
      headers: { accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as CustomerInventoryOverlayFile & { ok?: boolean };
    if (json?.schema_version !== "customer_availability_overlay_v1") return null;
    return json;
  } catch {
    return null;
  }
}

export async function loadCustomerInventoryOverlay(
  opts?: { force?: boolean; cacheVersion?: string | null },
): Promise<CustomerInventoryOverlayFile | null> {
  if (opts?.force) invalidateCustomerInventoryOverlayCache();
  if (opts?.cacheVersion && opts.cacheVersion !== overlayCacheVersion) {
    invalidateCustomerInventoryOverlayCache();
  }
  if (!overlayCache) {
    overlayCache = fetchOverlay(opts?.cacheVersion).then((file) => {
      overlayCacheVersion = file?.cache_version ?? null;
      return file;
    });
  }
  return overlayCache;
}

export function applyInventoryOverlayToProduct(
  product: CatalogV2Product,
  overlay: CustomerInventoryOverlayFile | null,
): CatalogV2Product {
  if (!overlay) return product;
  const row = overlay.by_product[product.id];
  if (!row) return product;
  const available = Math.max(0, Math.trunc(Number(row.available_quantity) || 0));
  const stockStatus =
    row.stock_status === "preorder" || row.stock_status === "discontinued"
      ? row.stock_status
      : available <= 0
        ? "out_of_stock"
        : available <= 3
          ? "low_stock"
          : "in_stock";
  return {
    ...product,
    stockStatus,
    // Keep sellable false only when out; do not remove from catalog.
    isSellable: stockStatus === "out_of_stock" || stockStatus === "discontinued" ? false : product.isSellable,
  };
}

export async function enrichCatalogWithInventoryOverlay(
  products: CatalogV2Product[],
  opts?: { force?: boolean; cacheVersion?: string | null },
): Promise<CatalogV2Product[]> {
  const overlay = await loadCustomerInventoryOverlay(opts);
  if (!overlay) return products;
  return products.map((p) => applyInventoryOverlayToProduct(p, overlay));
}

export function customerLabelFromOverlayRow(row: CustomerInventoryOverlayRow | null | undefined): string {
  if (!row) return "Наявність уточнюється";
  if (row.stock_status === "preorder") return "Під замовлення";
  if (row.stock_status === "discontinued") return "Знято з продажу";
  const available = Math.max(0, Math.trunc(Number(row.available_quantity) || 0));
  if (available <= 0) return "Немає в наявності";
  return "Є в наявності";
}
